import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  Visibility as ViewsIcon,
  ThumbUp as LikesIcon,
  Share as SharesIcon,
  Comment as CommentsIcon,
  PlayCircle as PlaysIcon,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { statsService, projectService } from '../services/api';

interface MetricCard {
  title: string;
  value: number | string;
  change: number;
  icon: React.ReactNode;
  color: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Analytics: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState(7);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [projects, setProjects] = useState<any[]>([]);
  
  // Analytics data
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [performanceData, setPerformanceData] = useState<any[]>([]);
  const [platformData, setPlatformData] = useState<any[]>([]);
  const [bestPerformingReels, setBestPerformingReels] = useState<any[]>([]);

  useEffect(() => {
    fetchProjects();
    fetchAnalytics();
  }, [timeRange, selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await projectService.list();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats
      const dashboardResponse = await statsService.dashboard(timeRange);
      setDashboardData(dashboardResponse.data);
      
      // Fetch performance trends
      const trendsResponse = await statsService.trends({
        days: timeRange,
        project_id: selectedProject !== 'all' ? parseInt(selectedProject) : undefined,
      });
      setPerformanceData(trendsResponse.data?.daily_stats || []);
      setPlatformData(trendsResponse.data?.platform_breakdown || []);
      
      // Fetch best performing reels
      const bestResponse = await statsService.bestPerforming(
        'views',
        10,
        selectedProject !== 'all' ? parseInt(selectedProject) : undefined
      );
      setBestPerformingReels(bestResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Mock data for demonstration
      setMockData();
    } finally {
      setLoading(false);
    }
  };

  const setMockData = () => {
    // Mock dashboard data
    setDashboardData({
      total_reels: 45,
      total_views: 125000,
      total_likes: 8500,
      total_shares: 1200,
      total_comments: 450,
      avg_engagement_rate: 7.2,
      views_change: 15.5,
      likes_change: 12.3,
      shares_change: 8.7,
      comments_change: -2.1,
    });
    
    // Mock performance data
    const mockPerformance = [];
    for (let i = timeRange - 1; i >= 0; i--) {
      mockPerformance.push({
        date: format(subDays(new Date(), i), 'MMM dd'),
        views: Math.floor(Math.random() * 20000) + 5000,
        likes: Math.floor(Math.random() * 1500) + 300,
        shares: Math.floor(Math.random() * 300) + 50,
        comments: Math.floor(Math.random() * 100) + 20,
      });
    }
    setPerformanceData(mockPerformance);
    
    // Mock platform data
    setPlatformData([
      { platform: 'Instagram', views: 45000, percentage: 36 },
      { platform: 'TikTok', views: 38000, percentage: 30.4 },
      { platform: 'YouTube', views: 25000, percentage: 20 },
      { platform: 'Facebook', views: 12000, percentage: 9.6 },
      { platform: 'Twitter', views: 5000, percentage: 4 },
    ]);
    
    // Mock best performing reels
    setBestPerformingReels([
      { id: 1, title: 'Top 5 AI Tools for 2024', views: 25000, likes: 1800, engagement_rate: 8.5 },
      { id: 2, title: 'Morning Routine Tips', views: 22000, likes: 1600, engagement_rate: 7.8 },
      { id: 3, title: 'Quick Recipe Ideas', views: 18000, likes: 1400, engagement_rate: 7.2 },
      { id: 4, title: 'Tech News Update', views: 15000, likes: 1100, engagement_rate: 6.9 },
      { id: 5, title: 'Fitness Challenge Day 1', views: 12000, likes: 950, engagement_rate: 6.5 },
    ]);
  };

  const getMetricCards = (): MetricCard[] => {
    if (!dashboardData) return [];
    
    return [
      {
        title: 'Total Views',
        value: dashboardData.total_views?.toLocaleString() || '0',
        change: dashboardData.views_change || 0,
        icon: <ViewsIcon />,
        color: theme.palette.primary.main,
      },
      {
        title: 'Total Likes',
        value: dashboardData.total_likes?.toLocaleString() || '0',
        change: dashboardData.likes_change || 0,
        icon: <LikesIcon />,
        color: theme.palette.success.main,
      },
      {
        title: 'Total Shares',
        value: dashboardData.total_shares?.toLocaleString() || '0',
        change: dashboardData.shares_change || 0,
        icon: <SharesIcon />,
        color: theme.palette.info.main,
      },
      {
        title: 'Total Comments',
        value: dashboardData.total_comments?.toLocaleString() || '0',
        change: dashboardData.comments_change || 0,
        icon: <CommentsIcon />,
        color: theme.palette.warning.main,
      },
    ];
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Analytics
          </Typography>
          
          <Box display="flex" gap={2}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                label="Project"
              >
                <MenuItem value="all">All Projects</MenuItem>
                {projects && Array.isArray(projects) ? projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                )) : null}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as number)}
                label="Time Range"
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Metric Cards */}
        <Grid container spacing={3} mb={3}>
          {getMetricCards().map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        {metric.title}
                      </Typography>
                      <Typography variant="h4">
                        {metric.value}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        <TrendingUpIcon
                          sx={{
                            fontSize: 16,
                            color: metric.change >= 0 ? 'success.main' : 'error.main',
                            transform: metric.change >= 0 ? 'none' : 'rotate(180deg)',
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: metric.change >= 0 ? 'success.main' : 'error.main',
                            ml: 0.5,
                          }}
                        >
                          {Math.abs(metric.change)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" ml={0.5}>
                          vs last period
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: `${metric.color}20`,
                        borderRadius: 2,
                        p: 1,
                        color: metric.color,
                      }}
                    >
                      {metric.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Charts */}
        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Performance Trends" />
            <Tab label="Platform Breakdown" />
            <Tab label="Top Performing Reels" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>
              Engagement Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="views" stroke="#8884d8" name="Views" />
                <Line type="monotone" dataKey="likes" stroke="#82ca9d" name="Likes" />
                <Line type="monotone" dataKey="shares" stroke="#ffc658" name="Shares" />
                <Line type="monotone" dataKey="comments" stroke="#ff7c7c" name="Comments" />
              </LineChart>
            </ResponsiveContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Views by Platform
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={platformData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.platform}: ${entry.percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="views"
                    >
                      {platformData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Platform Performance
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={platformData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="platform" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="views" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>
              Best Performing Reels
            </Typography>
            <Grid container spacing={2}>
              {bestPerformingReels && Array.isArray(bestPerformingReels) ? bestPerformingReels.map((reel, index) => (
                <Grid item xs={12} key={reel.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6">
                            #{index + 1} {reel.title}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={3}>
                          <Box textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                              Views
                            </Typography>
                            <Typography variant="h6">
                              {reel.views.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                              Likes
                            </Typography>
                            <Typography variant="h6">
                              {reel.likes.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                              Engagement
                            </Typography>
                            <Typography variant="h6" color="primary">
                              {reel.engagement_rate}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No performance data available
                  </Typography>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </Paper>

        {/* Additional Stats */}
        <Grid container spacing={3} mt={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Average Engagement Rate
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h2" color="primary">
                    {dashboardData?.avg_engagement_rate || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Across all platforms
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Reels Published
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h2" color="primary">
                    {dashboardData?.total_reels || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In selected time period
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Analytics;